function _classPrivateFieldInitSpec(obj, privateMap, value) { _checkPrivateRedeclaration(obj, privateMap); privateMap.set(obj, value); }
function _checkPrivateRedeclaration(obj, privateCollection) { if (privateCollection.has(obj)) { throw new TypeError("Cannot initialize the same private elements twice on an object"); } }
function _classPrivateFieldGet(receiver, privateMap) { var descriptor = _classExtractFieldDescriptor(receiver, privateMap, "get"); return _classApplyDescriptorGet(receiver, descriptor); }
function _classApplyDescriptorGet(receiver, descriptor) { if (descriptor.get) { return descriptor.get.call(receiver); } return descriptor.value; }
function _classPrivateFieldSet(receiver, privateMap, value) { var descriptor = _classExtractFieldDescriptor(receiver, privateMap, "set"); _classApplyDescriptorSet(receiver, descriptor, value); return value; }
function _classExtractFieldDescriptor(receiver, privateMap, action) { if (!privateMap.has(receiver)) { throw new TypeError("attempted to " + action + " private field on non-instance"); } return privateMap.get(receiver); }
function _classApplyDescriptorSet(receiver, descriptor, value) { if (descriptor.set) { descriptor.set.call(receiver, value); } else { if (!descriptor.writable) { throw new TypeError("attempted to set read only private field"); } descriptor.value = value; } }
import { assertNonNullable } from './assert';
const BUSINESS_BANKING_CARD_SIGNATURE_RECT_WIDTH = 1062;
const BUSINESS_BANKING_CARD_SIGNATURE_RECT_HEIGHT = 354;
const SIGNATURE_PADDING = 4;
export const BUSINESS_BANKING_CARD_SIGNATURE_RECT = {
  width: BUSINESS_BANKING_CARD_SIGNATURE_RECT_WIDTH,
  height: BUSINESS_BANKING_CARD_SIGNATURE_RECT_HEIGHT
};

/**
 * Returns a DOMString-compatible string with CSS font specifier syntax
 * describing the given font-size and font-family.
 */
export function fontString(fontSize, fontFamily) {
  return `${fontSize}px ${fontFamily}`;
}

/**
 * Returns whether the first given rectangle dimensions can contain the second
 */
export function contains(a, b) {
  return a.height >= b.height && a.width >= b.width;
}

/**
 * Created measurements for text/font on a canvas configured for text-align
 * right and justified middle text within canvas bounds.
 *
 * TODO(steckel): Report errors.
 *
 * This relies upon browser support for TextMetrics' properties
 * actualBoundingBoxAscent, actualBoundingBoxDescent, actualBoundingBoxLeft and
 * actualBoundingBoxRight.
 *
 * At the time of writing this, Dashboard browser
 * support does not include browsers where this API is unavailable, but it
 * should be noted that it can throw an Error in the environments where it is
 * not.
 *
 * Additionally, the functions in this file and TextRenderer and
 * ITextRenderingStrategy are organized in a way that would allow "more
 * intelligent" rendering strategies to implement backwards-compatible
 * measuring logic if need be.
 */
export function measureCanvasText(context, font, text) {
  context.textAlign = 'right';
  context.textBaseline = 'middle';
  context.font = font;
  const {
    actualBoundingBoxAscent,
    actualBoundingBoxDescent,
    actualBoundingBoxLeft,
    actualBoundingBoxRight
  } = context.measureText(text);
  if (actualBoundingBoxAscent == null || actualBoundingBoxDescent == null || actualBoundingBoxLeft == null || actualBoundingBoxRight == null) {
    throw new Error('TextMetrics does not support required actualBoundingBox properties');
  }
  const height = Math.ceil(actualBoundingBoxAscent + actualBoundingBoxDescent) + SIGNATURE_PADDING;
  const width = Math.ceil(actualBoundingBoxLeft + actualBoundingBoxRight) + SIGNATURE_PADDING;
  const x = context.canvas.width - Math.ceil(actualBoundingBoxRight) - SIGNATURE_PADDING / 2;
  const y = (context.canvas.height - height) / 2 + Math.ceil(actualBoundingBoxAscent) + SIGNATURE_PADDING / 2;
  return {
    coordinates: {
      x,
      y
    },
    rect: {
      width,
      height
    }
  };
}
/**
 * Linearly increases/decreases the fontSize until it finds an augmentation
 * that no longer fits within the target bounding box.
 *
 * This method can take O(n+2) time where n is the distance between fontSize
 * and the ideal font size.
 *
 * The time complexity can surely be tweaked, but the use-case should result in
 * more iterations up front and fewer if incrementally changing text (e.g.
 * typing the name 'Bobert' and passing in the previous calculated font size as
 * the fontSize upon each letter entry).
 *
 */
export function calculateParameters(context, fontFamily, fontSize, measureTextFn, text) {
  if (fontSize <= 0 || fontSize > Number.MAX_VALUE) {
    throw new RangeError('Calculated font size fell outside bounds before reaching a solution.');
  }
  if (!/\S/.test(text)) {
    return {
      fontFamily,
      fontSize,
      coordinates: {
        x: 0,
        y: 0
      }
    };
  }
  const font = fontString(fontSize, fontFamily);
  const targetSize = {
    width: context.canvas.width,
    height: context.canvas.height
  };
  const actualSize = measureTextFn(context, font, text);
  const fits = contains(targetSize, actualSize.rect);
  if (fits) {
    const increasedFontSize = fontSize + 1;
    const increasedFont = fontString(increasedFontSize, fontFamily);
    const increasedActualSize = measureTextFn(context, increasedFont, text);
    const increasedFits = contains(targetSize, increasedActualSize.rect);
    if (increasedFits) {
      return calculateParameters(context, fontFamily, fontSize + 2, measureTextFn, text);
    }

    // if this size fits, but n + 1 doesn't, we found our match
    return {
      fontFamily,
      fontSize,
      coordinates: actualSize.coordinates
    };
  }
  const decreasedFontSize = fontSize - 1;
  const decreasedFont = fontString(decreasedFontSize, fontFamily);
  const decreasedActualSize = measureTextFn(context, decreasedFont, text);
  const decreasedFits = contains(targetSize, decreasedActualSize.rect);
  if (decreasedFits) {
    // if this size doesn't fit, but n - 1 does, we found our match
    return {
      fontFamily,
      fontSize: decreasedFontSize,
      coordinates: decreasedActualSize.coordinates
    };
  }
  return calculateParameters(context, fontFamily, fontSize - 2, measureTextFn, text);
}

/**
 * The default signature strategy maximizes the font size to fill the height of
 * the available bounding box, text-align right and text-justified in the
 * middle.
 */
export const BUSINESS_BANKING_LOWER_RIGHT_RENDER_STRATEGY = {
  calculateTextRenderingParams(context, fontSize, fontFamily, text) {
    return calculateParameters(context, fontFamily, fontSize, measureCanvasText, text);
  },
  createCanvas() {
    return document.createElement('canvas');
  }
};

/**
 * TextRenderer class is intended to manage state for executing a given text
 * rendering strategy and provide opportunity for incremental text rendering
 * calculation.
 */
var _canvas = /*#__PURE__*/new WeakMap();
var _context = /*#__PURE__*/new WeakMap();
var _textRenderingParams = /*#__PURE__*/new WeakMap();
var _strategy = /*#__PURE__*/new WeakMap();
var _text = /*#__PURE__*/new WeakMap();
export default class TextRenderer {
  constructor(estimatedfontSize, fontFamily, text, boundingBox, strategy) {
    _classPrivateFieldInitSpec(this, _canvas, {
      writable: true,
      value: void 0
    });
    _classPrivateFieldInitSpec(this, _context, {
      writable: true,
      value: void 0
    });
    _classPrivateFieldInitSpec(this, _textRenderingParams, {
      writable: true,
      value: void 0
    });
    _classPrivateFieldInitSpec(this, _strategy, {
      writable: true,
      value: void 0
    });
    _classPrivateFieldInitSpec(this, _text, {
      writable: true,
      value: void 0
    });
    _classPrivateFieldSet(this, _canvas, strategy.createCanvas());
    _classPrivateFieldGet(this, _canvas).height = boundingBox.height;
    _classPrivateFieldGet(this, _canvas).width = boundingBox.width;
    _classPrivateFieldSet(this, _context, assertNonNullable(_classPrivateFieldGet(this, _canvas).getContext('2d')));
    _classPrivateFieldSet(this, _strategy, strategy);
    _classPrivateFieldSet(this, _text, text);
    _classPrivateFieldSet(this, _textRenderingParams, _classPrivateFieldGet(this, _strategy).calculateTextRenderingParams(_classPrivateFieldGet(this, _context), estimatedfontSize, fontFamily, text));
  }
  get fontFamily() {
    return _classPrivateFieldGet(this, _textRenderingParams).fontFamily;
  }
  set fontFamily(newFontFamily) {
    if (_classPrivateFieldGet(this, _textRenderingParams).fontFamily !== newFontFamily) {
      const {
        fontSize
      } = _classPrivateFieldGet(this, _textRenderingParams);
      _classPrivateFieldSet(this, _textRenderingParams, _classPrivateFieldGet(this, _strategy).calculateTextRenderingParams(_classPrivateFieldGet(this, _context), fontSize, newFontFamily, _classPrivateFieldGet(this, _text)));
    }
  }
  get text() {
    return _classPrivateFieldGet(this, _text);
  }
  set text(newText) {
    if (_classPrivateFieldGet(this, _text) !== newText) {
      _classPrivateFieldSet(this, _text, newText);
      const {
        fontFamily,
        fontSize
      } = _classPrivateFieldGet(this, _textRenderingParams);
      _classPrivateFieldSet(this, _textRenderingParams, _classPrivateFieldGet(this, _strategy).calculateTextRenderingParams(_classPrivateFieldGet(this, _context), fontSize, fontFamily, _classPrivateFieldGet(this, _text)));
    }
  }
  toDataURL() {
    this.render();
    return _classPrivateFieldGet(this, _canvas).toDataURL();
  }
  toBytes() {
    this.render();
    return new Promise((resolve, reject) => {
      try {
        _classPrivateFieldGet(this, _canvas).toBlob(blob => resolve(blob), 'image/png');
      } catch (e) {
        reject(e);
      }
    }).then(blob => blob ?? new Blob()).then(blob => blob.arrayBuffer()).then(buffer => new Uint8Array(buffer));
  }
  render() {
    const context = assertNonNullable(_classPrivateFieldGet(this, _canvas).getContext('2d'));
    context.clearRect(0, 0, _classPrivateFieldGet(this, _canvas).width, _classPrivateFieldGet(this, _canvas).height);
    if (/\S/.test(_classPrivateFieldGet(this, _text))) {
      const {
        fontFamily,
        fontSize,
        coordinates
      } = _classPrivateFieldGet(this, _textRenderingParams);
      context.font = fontString(fontSize, fontFamily);
      context.fillText(_classPrivateFieldGet(this, _text), coordinates.x, coordinates.y);
    }
  }
}